import { auth } from '../config/firebase'
import { API_BASE_URL } from '../config/api'

// Types

export interface TicketResponse {
  id: string
  ticket_id: string
  responder_type: 'ai' | 'human' | 'system'
  responder_id?: string
  responder_name?: string
  content: string
  is_internal: boolean
  ai_confidence?: number
  created_at: string
}

export interface TicketAttachment {
  id: string
  ticket_id: string
  filename: string
  mime_type: string
  size_bytes: number
  /** Signed URL for download — populated by the backend when the upload endpoint ships. */
  url?: string
  created_at: string
}

export interface SupportTicket {
  id: string
  ticket_number: string
  user_id: string
  organization_id?: string
  project_id?: string
  subject: string
  description: string
  category: string
  status: string
  priority: string
  source: string
  assigned_to?: string
  assignee_name?: string
  due_date?: string
  first_response_at?: string
  resolved_at?: string
  created_at: string
  updated_at: string
  responses?: TicketResponse[]
  /** File attachments — returned by the API once the upload endpoint ships (ops-agent). */
  attachments?: TicketAttachment[]
}

export interface TicketListItem {
  id: string
  ticket_number: string
  subject: string
  category: string
  status: string
  priority: string
  source: string
  due_date?: string
  created_at: string
  updated_at: string
  response_count: number
}

export interface CreateTicketRequest {
  subject: string
  description: string
  category?: string
  priority?: string
  project_id?: string
}

export interface ProjectInfo {
  id: string
  name: string
  short_uuid: string
  access_level: string
}

// HTTP client

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser
  if (!user) throw new Error('Not authenticated')
  const token = await user.getIdToken()
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders()
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`API ${res.status}: ${body}`)
  }
  return res.json()
}

// API methods

export const supportApi = {
  // Projects
  getMyProjects(): Promise<ProjectInfo[]> {
    return apiFetch('/api/support/my-projects')
  },

  // Tickets
  listProjectTickets(
    projectId: string,
    params?: { status?: string; limit?: number; offset?: number }
  ): Promise<TicketListItem[]> {
    const query = new URLSearchParams()
    if (params?.status) query.set('status', params.status)
    if (params?.limit) query.set('limit', String(params.limit))
    if (params?.offset) query.set('offset', String(params.offset))
    const qs = query.toString()
    return apiFetch(`/api/support/tickets/by-project/${projectId}${qs ? `?${qs}` : ''}`)
  },

  getTicket(ticketId: string): Promise<SupportTicket> {
    return apiFetch(`/api/support/tickets/${ticketId}`)
  },

  createTicket(data: CreateTicketRequest): Promise<SupportTicket> {
    return apiFetch('/api/support/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  addResponse(ticketId: string, content: string): Promise<TicketResponse> {
    return apiFetch(`/api/support/tickets/${ticketId}/responses`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    })
  },

  updateStatus(ticketId: string, status: string): Promise<SupportTicket> {
    return apiFetch(`/api/support/tickets/${ticketId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })
  },

  // Attachments
  async uploadAttachment(ticketId: string, file: File): Promise<TicketAttachment> {
    const user = auth.currentUser
    if (!user) throw new Error('Not authenticated')
    const token = await user.getIdToken()
    const form = new FormData()
    form.append('file', file)
    const res = await fetch(`${API_BASE_URL}/api/support/tickets/${ticketId}/attachments`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!res.ok) {
      const body = await res.text()
      throw new Error(`API ${res.status}: ${body}`)
    }
    return res.json()
  },

  getAttachmentSignedUrl(ticketId: string, attachmentId: string): Promise<{ url: string }> {
    return apiFetch(`/api/support/tickets/${ticketId}/attachments/${attachmentId}/signed-url`)
  },

  // Auth - register with backend after Firebase sign-in
  async registerWithBackend(): Promise<{ access_token: string; user: Record<string, unknown> }> {
    const user = auth.currentUser
    if (!user) throw new Error('Not authenticated')
    const idToken = await user.getIdToken()
    return apiFetch('/api/auth/firebase', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    })
  },
}

// ─── KB types ─────────────────────────────────────────────────────────────────

export interface KBArticleListItem {
  id: string
  title: string
  category: string
  excerpt?: string
  created_at: string
  updated_at: string
}

export interface KBArticle extends KBArticleListItem {
  content: string
}

// ─── KB API ───────────────────────────────────────────────────────────────────

export const kbApi = {
  listArticles(params?: { category?: string; q?: string }): Promise<KBArticleListItem[]> {
    const q = new URLSearchParams()
    if (params?.category) q.set('category', params.category)
    if (params?.q) q.set('q', params.q)
    const qs = q.toString()
    return apiFetch(`/api/support/kb/articles${qs ? `?${qs}` : ''}`)
  },

  getArticle(articleId: string): Promise<KBArticle> {
    return apiFetch(`/api/support/kb/articles/${articleId}`)
  },
}

// ─── Admin types ──────────────────────────────────────────────────────────────

export interface AdminStats {
  total_tickets: number
  open_tickets: number
  unassigned_tickets: number
  avg_response_time_hours: number | null
  by_status: Record<string, number>
  by_category: Record<string, number>
  by_priority: Record<string, number>
}

export interface AdminTicketListItem extends TicketListItem {
  user_email?: string
  assigned_to?: string
  assignee_name?: string
  first_response_at?: string
  resolved_at?: string
}

// ─── Admin API ────────────────────────────────────────────────────────────────

export const adminApi = {
  getStats(): Promise<AdminStats> {
    return apiFetch('/api/support/admin/stats')
  },

  listTickets(params?: {
    status?: string
    category?: string
    priority?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<AdminTicketListItem[]> {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.category) q.set('category', params.category)
    if (params?.priority) q.set('priority', params.priority)
    if (params?.search) q.set('search', params.search)
    if (params?.limit) q.set('limit', String(params.limit))
    if (params?.offset) q.set('offset', String(params.offset))
    const qs = q.toString()
    return apiFetch(`/api/support/admin/tickets${qs ? `?${qs}` : ''}`)
  },

  getTicket(ticketId: string): Promise<SupportTicket> {
    return apiFetch(`/api/support/admin/tickets/${ticketId}`)
  },

  updateStatus(ticketId: string, status: string): Promise<SupportTicket> {
    return apiFetch(`/api/support/admin/tickets/${ticketId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    })
  },

  addResponse(ticketId: string, content: string, isInternal = false): Promise<TicketResponse> {
    return apiFetch(`/api/support/admin/tickets/${ticketId}/responses`, {
      method: 'POST',
      body: JSON.stringify({ content, is_internal: isInternal }),
    })
  },
}
